import type { ConversationPlan } from '@knownow/domain'
import { ValidationError, normalizeLanguageCode } from '@knownow/domain'
import type { IConversationPlanRepository } from '@knownow/domain'

export interface CreatePlanInput {
  readonly title: string
  readonly description?: string
  readonly timeBudgetMinutes?: number
  /** Preferred language for this plan. When omitted the DB trigger inherits
   *  the owning organization's `preferred_language`. */
  readonly preferredLanguage?: string
}

export class CreatePlanUseCase {
  constructor(private readonly repo: IConversationPlanRepository) {}

  async execute(input: CreatePlanInput): Promise<ConversationPlan> {
    const title = input.title.trim()
    if (!title) {
      throw new ValidationError('Plan title must not be empty')
    }

    // When caller omits preferredLanguage we send 'en' — the DB trigger
    // replaces it with the org's language if the org has a non-'en' default.
    const preferredLanguage = input.preferredLanguage !== undefined
      ? normalizeLanguageCode(input.preferredLanguage)
      : 'en'

    const now = new Date()
    const plan: ConversationPlan = {
      id: '',
      organizationId: '',
      version: 0,
      title,
      description: input.description?.trim() ?? '',
      status: 'draft',
      timeBudgetMinutes: input.timeBudgetMinutes ?? 15,
      preferredLanguage,
      shareToken: null,
      deletedAt: null,
      createdAt: now,
      updatedAt: now,
      informationNeeds: [],
      strategy: null,
    }

    const id = await this.repo.save(plan)
    return { ...plan, id }
  }
}
